/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2017, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([
	'underscore',
	'../../../lib/@waca/core-client/js/core-client/ui/core/View',
	'../../../lib/@waca/dashboard-common/dist/utils/Flyout',
	'../../../widgets/livewidget/nls/StringResources',
	'text!./templates/InfoIndicator.html',
	'./InfoFlyoutView',
	'jquery',
	'doT'
], function(_, View, Flyout, stringResources, template, InfoFlyoutView, $, dot) {
	'use strict';
	let infoTemplate = dot.template(template);
	var infoIndicator = View.extend({
		templateString: template,

		init: function(options) {
			infoIndicator.inherited('init', this, arguments);

			const warnIcon = options.ownerWidget.dashboardApi.getFeature('Icons').getIcon('warnIcon');
			this.messages = {};
			this.$el = $(infoTemplate({
				title: stringResources.get('warning'),
				iconId: warnIcon.id
			}));
			let onclick = this._onClick.bind(this);
			this.$el.on('tap keydown click', onclick);

			this._hideIndicator();
		},

		remove: function() {
			if (this.flyout) {
				this.flyout.destroy();
			}
			infoIndicator.inherited('remove', this, arguments);
		},

		/**
		 * @function addInfo
		 * Adds an array of information to be rendered by the indicator.
		 * This can be called multiple times before calling {@link InfoIndicator#reset} and
		 * the information added with the same identifier will be merged as an unique information.
		 * Each array item must be provided in the following format.
		 * @param {Object[]} info - array of objects representing an information
		 * @example
		 * 	indicator.addInfo([{
		 * 		id: 'information-identifier',
		 *		label: 'information label'
		 *		items: [{
		 *			id: 'info-data-identifier',
		 *			label: 'information data label'
		 *		}],
		 *		small: true    //Change the max-height of the dialog content to 100px instead of 300x
		 * 	}]);
		 */
		addInfo: function(info) {
			// filter out empty messages
			var filtered = _.filter(info, (i) =>{
				return i.items.length > 0;
			});

			// merge the messages to the map
			// each service should has its own indicator section, and the message should be updated when that section get updated
			_.each(filtered, (i)=> {
				this.messages[i.id] = i;
			});

			if (_.isEmpty(this.messages)) {
				this._hideIndicator();
			} else {
				this.$el.show();
			}
		},

		/**
		 * @function reset
		 * Reset the information messages
		 */
		reset: function(){
			this.messages = {};
			this._hideIndicator();
		},

		/**
		* Hide the info indicator
		*/
		_hideIndicator() {
			this.$el.hide();
		},

		clearMessagesByIdAndSubtype(idType) {
			const id = idType.id;
			const type = idType.type;
			Object.values(this.messages).forEach(message => {
				for(let i = 0; i < message.items.length; i++) {
					if (message.items[i].id === id && message.items[i].type === type) {
						message.items.splice(i, 1);

						if (message.items.length === 0) {
							this.clearMessagesWithIds([message.id]);
						}
					}
				}
			});
		},

		/**
		* Clear all messages with the specified id. If there are no messages remaining
		* after the clear then hide the indicator.
		* @param {Array} ids - array of ids to clear in the messages object.
		*/
		clearMessagesWithIds(ids) {
			ids.forEach(id => {
				if (this.messages[id]) {
					delete this.messages[id];
				}
			});

			// Check if there are messages available, if not, hide
			if (_.isEmpty(this.messages)) {
				this._hideIndicator();
			}
		},

		_onClick: function(event) {
			if(event.type === 'keydown') {
				event.stopPropagation();
				var key = event.key || event.keyCode || event.which || '';
				if(key !== 'Enter' && key !== 13) {
					return;
				}
			}

			if(event.gesture) {
				event.gesture.preventDefault();
			}

			if(event.cancelable) {
				event.preventDefault();
			}

			if (!this.flyout) {
				this.flyout = new Flyout({
					alignment: 'center',
					selector: this.$el,
					flyoutClass: 'filterPopover visTopPopover',
					viewClass: InfoFlyoutView,
					viewOptions: {
						messages: _.values(this.messages)
					}
				});
			} else {
				this.flyout.view.setMessages(_.values(this.messages));
			}

			this.flyout.open();
			event.stopPropagation();
		}
	});

	return infoIndicator;
});
